import React, { useState, useEffect } from 'react'
import Wrapper from '../../components/Wrapper'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Grid, Card, CardContent, ToggleButton, ToggleButtonGroup, Paper, TablePagination, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Avatar, CircularProgress, FormControlLabel, Switch } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPenToSquare, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import { menu, input, sip, auto, did } from '../../translate'
import Api from '../../Api'
import { toast } from 'react-toastify'
import Loader from '../../components/Loader'
import TableLoader from '../../components/TableLoader'
import AssignedSearch from '../../components/new_component/AssignedSearch'
import { cardStyles, activitiesStyles, employeeTableStyles } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a'

function AutoCallList({ listType }) {
  const navigate = useNavigate()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const [viewMode, setViewMode] = useState('table')
  const [autoCalls, setAutoCalls] = useState([])
  const [filteredAutoCalls, setFilteredAutoCalls] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(9)
  const [tablePage, setTablePage] = useState(0)
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10)
  const [keepOnLeftMenu, setKeepOnLeftMenu] = useState(() => localStorage.getItem('auto_call_keep_on_left_menu') === 'true')

  const typeFilteredCalls = React.useMemo(() => {
    if (!listType) return autoCalls
    if (listType === 'autocall') return autoCalls.filter(item => item.isivr === 2)
    if (listType === 'group') return autoCalls.filter(item => item.isivr !== 2)
    return autoCalls
  }, [autoCalls, listType])

  const pageTitle = listType === 'autocall'
    ? menu.autoCall1[langIndex]
    : listType === 'group'
      ? menu.autoCall2[langIndex]
      : menu.autoCall[langIndex]

  useEffect(() => {
    fetchAutoCalls()
  }, [])

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const filtered = typeFilteredCalls.filter(item => {
        const type = item.isivr === 2 
          ? menu.autoCall1[langIndex] 
          : menu.autoCall2[langIndex]
        const searchableFields = [
          item.name,
          type,
          item.okqanak,
          item.rlimit,
          item.mnacord,
          item.autostatus ? auto.running[langIndex] : auto.paused[langIndex]
        ]
        return searchableFields.some(field => 
          field !== null && 
          field !== undefined && 
          String(field).toLowerCase().includes(query)
        )
      })
      setFilteredAutoCalls(filtered)
    } else {
      setFilteredAutoCalls(typeFilteredCalls)
    }
    setPage(0)
    setTablePage(0)
  }, [searchQuery, typeFilteredCalls, langIndex])

  const fetchAutoCalls = async () => {
    setLoading(true)
    try {
      const response = await Api.getAuto()
      if (response?.data?.status === 'ok') {
        const data = response.data.auto || []
        setAutoCalls(data)
        const filtered = !listType ? data : listType === 'autocall' ? data.filter(item => item.isivr === 2) : data.filter(item => item.isivr !== 2)
        setFilteredAutoCalls(filtered)
      }
    } catch (error) {
      console.error('Error fetching auto calls:', error)
      toast.error('Error fetching auto calls')
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value) => {
    setSearchQuery(value)
  }

  const handleAdd = () => {
    if (listType) {
      navigate(`/auto_call_single/add?campaignType=${encodeURIComponent(listType)}`)
    } else {
      navigate('/auto_call_single/add')
    }
  }

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode)
    }
  }

  const handleEdit = (id) => {
    if (listType) {
      navigate(`/auto_call_single/${id}?fromList=${encodeURIComponent(listType)}`)
    } else {
      navigate(`/auto_call_single/${id}`)
    }
  }

  const handleDelete = async (id) => {
    const deleteText = langIndex === 1 ? "Are you sure you want to delete?" :
      langIndex === 2 ? "Вы уверены, что хотите удалить?" :
        "Վստա՞հ եք, որ ցանկանում եք ջնջել"
    
    if (window.confirm(deleteText)) {
      try {
        await Api.deleteAuto(id)
        setAutoCalls(prev => prev.filter(item => item.id !== id))
        setFilteredAutoCalls(prev => prev.filter(item => item.id !== id))
        toast.success('Auto call deleted successfully')
      } catch (error) {
        console.error('Error deleting auto call:', error)
        toast.error('Error deleting auto call')
      }
    }
  }

  const handleStatus = async (id, status) => {
    const statusText = langIndex === 1 ? "Do you want to modify the specified row?" :
      langIndex === 2 ? "Вы хотите изменить указанную строку?" :
        "Դուք ցանկանու՞մ էք Փոփոխել նշված տողը"
    
    if (window.confirm(statusText)) {
      try {
        await Api.editAutoStatus(id, { status: +status === 0 ? 1 : 0 })
        setAutoCalls(prev => prev.map(item => 
          item.id === id ? { ...item, autostatus: +status === 0 ? 1 : 0 } : item
        ))
        setFilteredAutoCalls(prev => prev.map(item => 
          item.id === id ? { ...item, autostatus: +status === 0 ? 1 : 0 } : item
        ))
        toast.success('Status updated successfully')
      } catch (error) {
        console.error('Error updating status:', error)
        toast.error('Error updating status')
      }
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleTableChangePage = (event, newPage) => {
    setTablePage(newPage)
  }

  const handleTableChangeRowsPerPage = (event) => {
    setTableRowsPerPage(parseInt(event.target.value, 10))
    setTablePage(0)
  }

  const handleGoBack = () => {
    navigate('/short_function')
  }

  const paginatedCards = rowsPerPage > 0
    ? filteredAutoCalls.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredAutoCalls

  const paginatedTable = tableRowsPerPage > 0
    ? filteredAutoCalls.slice(tablePage * tableRowsPerPage, tablePage * tableRowsPerPage + tableRowsPerPage)
    : filteredAutoCalls

  return (
    <Wrapper>
      {viewMode === 'table' ? (
        <>
        
          <div className="tableParent">
            <Paper sx={{ p: 3, boxShadow: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, }}>
                  <IconButton
                    size="small"
                    sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
                    onClick={handleGoBack}
                    aria-label="Back"
                  >
                    <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                  </IconButton>
                  <Typography sx={{ fontWeight: 700, m: 0 }} variant="h5" component="h1">
                    {pageTitle}
                  </Typography>
                </Box>
                {listType !== 'group' && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={keepOnLeftMenu}
                        onChange={(e) => {
                          const value = e.target.checked
                          setKeepOnLeftMenu(value)
                          localStorage.setItem('auto_call_keep_on_left_menu', value ? 'true' : 'false')
                          if (value) {
                            localStorage.setItem('auto_call', 'true')
                          } else {
                            localStorage.removeItem('auto_call')
                          }
                        }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: color },
                        }}
                      />
                    }
                    label={langIndex === 1 ? 'Keep on the left menu' : langIndex === 2 ? 'Держать в левом меню' : 'Պահել ձախ մենյուում'}
                  />
                  {/* <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    aria-label="view mode"
                    size="small"
                  >
                    <ToggleButton value="table" aria-label="table view">
                      <ViewListIcon />
                    </ToggleButton>
                    <ToggleButton value="card" aria-label="card view">
                      <ViewModuleIcon />
                    </ToggleButton>
                  </ToggleButtonGroup> */}
                </Box>}
              </Box>
              <Box sx={{ mb: 3 }}>
                <AssignedSearch
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onAdd={handleAdd}
                  placeholder={input.search[langIndex]}
                  showAdd={true}
                  addColor={color}
                />
              </Box>
              {loading ? (
                <Box sx={employeeTableStyles.loadingBox}>
                  <CircularProgress sx={{ color }} />
                </Box>
              ) : paginatedTable.length > 0 ? (
                <>
                  <Box sx={activitiesStyles.activitiesContainer}>
                    {paginatedTable.map((item) => {
                      const type = item.isivr === 2 
                        ? menu.autoCall1[langIndex] 
                        : menu.autoCall2[langIndex]
                      const displayName = `${type} - ${item.name}`
                      const isRunning = item.autostatus
                      const isGroup = item.isivr !== 2
                      
                      return (
                        <Card key={item.id} sx={{ ...activitiesStyles.activityCard, '&:hover': { boxShadow: `0 8px 24px ${color}26`, borderColor: color } }}>
                          <CardContent sx={activitiesStyles.cardContent}>
                            <Box sx={activitiesStyles.cardContentBox}>
                              {isGroup ? (
                                <Avatar sx={activitiesStyles.getActivityAvatar(false)}>
                                  {((item.name && item.name[0]) || (type && type[0]) || 'G').toUpperCase()}
                                </Avatar>
                              ) : (
                                <Avatar sx={{
                                  ...activitiesStyles.getActivityAvatar(isRunning),
                                  ...(isRunning && { bgcolor: color, boxShadow: `0 4px 12px ${color}4D` }),
                                }}>
                                  {isRunning ? <PauseIcon sx={{ fontSize: 28 }} /> : <PlayArrowIcon sx={{ fontSize: 28 }} />}
                                </Avatar>
                              )}

                              <Box sx={activitiesStyles.activityContentBox}>
                                <Typography variant="h6" sx={activitiesStyles.activityTitle}>
                                  {displayName}
                                </Typography>

                                <Box sx={activitiesStyles.metadataBox}>
                                  <Box sx={activitiesStyles.metadataItem}>
                                    <CheckCircleIcon fontSize="small" sx={{ ...activitiesStyles.metadataIcon, mr: 0.5, color }} />
                                    <Typography variant="caption" sx={activitiesStyles.metadataText}>
                                      <strong>{auto.success[langIndex]}:</strong> {item.okqanak} / {item.dtmf} / {item.duration}
                                    </Typography>
                                  </Box>
                                  {item.rlimit ? (
                                    <Box sx={activitiesStyles.metadataItem}>
                                      <Typography variant="caption" sx={activitiesStyles.metadataText}>
                                        <strong>{auto.fail[langIndex]}:</strong> {item.rlimit}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {item.mnacord !== undefined ? (
                                    <Box sx={activitiesStyles.metadataItem}>
                                      <Typography variant="caption" sx={activitiesStyles.metadataText}>
                                        <strong>{auto.current[langIndex]}:</strong> {item.mnacord}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                </Box>
                              </Box>

                              <Box sx={employeeTableStyles.actionsContainer}>
                                {!isGroup && (
                                  isRunning ? (
                                    <CheckCircleIcon sx={employeeTableStyles.activeIcon(color)} />
                                  ) : (
                                    <CheckCircleOutlineIcon sx={{ color: '#9e9e9e', fontSize: 24 }} />
                                  )
                                )}
                                <Box sx={employeeTableStyles.actionButtonsBox}>
                                  {!isRunning ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(item.id)}
                                      sx={{
                                      color,
                                      '&:hover': { backgroundColor: `${color}15`, transform: 'scale(1.1)' },
                                      transition: 'all 0.2s ease',
                                      padding: '6px',
                                    }}
                                      aria-label="Edit"
                                    >
                                      <FontAwesomeIcon icon={faPenToSquare} />
                                    </IconButton>
                                  ) : null}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(item.id)}
                                    sx={employeeTableStyles.deleteIconButton}
                                    aria-label="Delete"
                                  >
                                    <FontAwesomeIcon icon={faTrashCan} />
                                  </IconButton>
                                </Box>
                                {!isGroup && (
                                  <Box
                                    onClick={() => handleStatus(item.id, item.autostatus)}
                                    sx={{
                                      cursor: 'pointer',
                                      mt: 0.5,
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: 1,
                                      display: 'inline-block',
                                      ...(isRunning
                                        ? {
                                            color: '#fff',
                                            backgroundColor: color,
                                            animation: 'statusPulse 1.5s ease-in-out infinite',
                                          }
                                        : {
                                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                            color: '#9e9e9e',
                                            transition: 'all 0.2s ease',
                                            animation: 'statusVibrate 1.5s ease-in-out infinite',
                                            '&:hover': { backgroundColor: `${color}15`, color },
                                          }),
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: isRunning ? '#fff' : '#9e9e9e' }}>
                                      {item.autostatus ? auto.running[langIndex] : auto.paused[langIndex]}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </Box>
                  <Box sx={{ ...employeeTableStyles.footerBox, justifyContent: 'flex-end' }}>
                    <TablePagination
                      component="div"
                      count={filteredAutoCalls.length}
                      rowsPerPage={tableRowsPerPage}
                      page={tablePage}
                      onPageChange={handleTableChangePage}
                      onRowsPerPageChange={handleTableChangeRowsPerPage}
                      labelRowsPerPage={input.rowsPerPage[langIndex]}
                      rowsPerPageOptions={[10, 25, 100, { label: did.all[langIndex], value: -1 }]}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={employeeTableStyles.emptyStateBox}>
                  <Typography variant="body1" color="text.secondary">
                    {auto.no_found[langIndex]}
                  </Typography>
                </Box>
              )}
            </Paper>
          </div>
        </>
      ) : (
        <div className="tableParent">
         
        </div>
      )}
        </Wrapper>
    )
}

export default AutoCallList
